/**
 * Heals workers assigned to rest
 * @param {Array} workers - Array of worker objects
 * @param {Object} assignments - Map of worker ID to task
 * @returns {Array} Updated workers array
 */
export function healRestingWorkers(workers, assignments) {
  return workers.map(w => {
    // Injured workers on rest heal to healthy
    if (w.status === 'injured' && assignments[w.id] === 'rest') {
      return { ...w, status: 'healthy' };
    }
    // Sick workers have a 20% chance to recover naturally when resting
    if (w.status === 'sick' && assignments[w.id] === 'rest' && Math.random() < 0.2) {
      return { ...w, status: 'healthy' };
    }
    return w;
  });
}

/**
 * Applies starvation damage to workers
 * @param {Array} workers - Array of worker objects
 * @param {number} starvationCount - Number of starvation instances to apply
 * @returns {Array} Updated workers array
 */
export function applyStarvation(workers, starvationCount) {
  let newWorkers = [...workers];

  for (let i = 0; i < starvationCount; i++) {
    const alive = newWorkers.filter(w => w.status !== 'dead');
    if (alive.length === 0) break;
    const target = alive[Math.floor(Math.random() * alive.length)];
    newWorkers = newWorkers.map(w =>
      w.id === target.id
        ? { ...w, status: w.status === 'injured' ? 'dead' : 'injured' }
        : w
    );
  }

  return newWorkers;
}

/**
 * Injures a worker (from failed push or combat)
 * @param {Array} workers - Array of worker objects
 * @param {number} workerId - ID of worker to injure
 * @param {boolean} severe - If true, injured workers die
 * @returns {Array} Updated workers array
 */
export function injureWorker(workers, workerId, severe = false) {
  return workers.map(w => {
    if (w.id === workerId) {
      if (severe && w.status === 'injured') {
        return { ...w, status: 'dead' };
      }
      return { ...w, status: 'injured' };
    }
    return w;
  });
}

/**
 * Creates a new worker
 * @param {number} id - Worker ID
 * @param {number} currentDay - Day worker joined
 * @param {string} type - Worker type ('worker', 'scholar', 'soldier')
 * @returns {Object} New worker object
 */
export function createWorker(id, currentDay = 1, type = 'worker') {
  return {
    id,
    status: 'healthy',
    type,
    joinedOn: currentDay
  };
}

/**
 * Creates an immature worker (child) that will mature after X days
 * @param {number} id - Worker ID
 * @param {number} currentDay - Current game day
 * @param {number} maturityDays - Days until maturation (5 default, 3 with nursery)
 * @returns {Object} Immature worker object
 */
export function createImmatureWorker(id, currentDay, maturityDays = 5) {
  return {
    id,
    status: 'immature',
    type: 'worker',
    maturesOn: currentDay + maturityDays,
    joinedOn: currentDay
  };
}

/**
 * Matures workers who have reached their maturity day
 * @param {Array} workers - Array of worker objects
 * @param {number} currentDay - Current game day
 * @returns {Array} Updated workers array
 */
export function matureWorkers(workers, currentDay) {
  return workers.map(w => {
    if (w.status === 'immature' && w.maturesOn <= currentDay) {
      return { ...w, status: 'healthy', maturesOn: null };
    }
    return w;
  });
}

/**
 * Adds refugee workers to the settlement
 * @param {Array} workers - Current workers array
 * @param {number} count - Number of refugees
 * @param {number} currentDay - Current game day
 * @returns {Array} Updated workers array with new refugees
 */
export function addRefugees(workers, count, currentDay) {
  const startId = Math.max(...workers.map(w => typeof w.id === 'number' ? w.id : 0)) + 1;
  const refugees = Array.from({ length: count }, (_, i) =>
    createWorker(startId + i, currentDay)
  );
  return [...workers, ...refugees];
}

/**
 * Removes a defecting worker from the settlement
 * @param {Array} workers - Current workers array
 * @param {number|string} workerId - ID of worker to remove
 * @returns {Array} Updated workers array without the defector
 */
export function removeDefector(workers, workerId) {
  return workers.filter(w => w.id !== workerId);
}

/**
 * Marks a worker as sick
 * @param {Array} workers - Array of worker objects
 * @param {number} workerId - ID of worker to make sick
 * @returns {Array} Updated workers array
 */
export function makeWorkerSick(workers, workerId) {
  return workers.map(w =>
    w.id === workerId && w.status === 'healthy'
      ? { ...w, status: 'sick' }
      : w
  );
}

/**
 * Attempts to heal sick workers based on treating workers
 * @param {Array} workers - Array of worker objects
 * @param {Object} assignments - Map of worker ID to task
 * @param {Object} rolls - Dice roll results (must include .treat if any workers treating)
 * @returns {Array} Updated workers array
 */
export function healSickWorkers(workers, assignments, rolls) {
  const sickWorkers = workers.filter(w => w.status === 'sick');
  if (sickWorkers.length === 0) return workers;

  // Get treat task data
  const treatData = rolls.treat;
  if (!treatData || treatData.rolls.length === 0) return workers;

  // Each treating worker gives sick workers a chance to heal
  // Heal chance = TREAT_HEAL_CHANCE% per treating worker
  const TREAT_HEAL_CHANCE = 40; // 40% per treating worker
  let updatedWorkers = [...workers];

  sickWorkers.forEach(sickWorker => {
    const healRoll = Math.random() * 100;
    const healChance = TREAT_HEAL_CHANCE * treatData.rolls.length;

    if (healRoll < healChance) {
      updatedWorkers = updatedWorkers.map(w =>
        w.id === sickWorker.id
          ? { ...w, status: 'healthy' }
          : w
      );
    }
  });

  return updatedWorkers;
}

/**
 * Checks if a worker can work (not dead or sick)
 * @param {Object} worker - Worker object
 * @returns {boolean} True if worker can work
 */
export function canWorkerWork(worker) {
  return worker.status !== 'dead' && worker.status !== 'sick';
}
